/**
 * Marketplace grid for one plugin kind (capabilities / tools). Owns:
 *
 *   - the search box + filter chips
 *   - one shared poll over `install-jobs/active` and
 *     `environment-jobs/active`, kept up while any job of either kind is
 *     in flight, so the catalog is revalidated the moment the last job
 *     ends and every card's own state refreshes
 *   - a responsive grid of `MarketplaceCard`s
 *
 * Cards drive their own install/uninstall mutations and derive their
 * state from their own `latest_job`; nothing propagates between cards.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  listActiveInstallJobs,
  listCatalog,
  type ListCatalogParams,
} from '@optum/api-client/endpoints/catalog';
import { listActiveEnvironmentJobs } from '@optum/api-client/endpoints/environment';
import type { CatalogEntryOut, CatalogKind } from '@optum/api-client/types';

import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';

import { MarketplaceCard } from './MarketplaceCard.js';

type Filter = 'all' | 'installed' | 'not_installed' | 'upgrade_available';

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'not_installed', label: 'Not installed' },
  { key: 'installed', label: 'Installed' },
  { key: 'upgrade_available', label: 'Upgrade available' },
];

export function MarketplaceGrid({ kind }: { kind: CatalogKind }) {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const params: ListCatalogParams = {};
  const trimmed = query.trim();
  if (trimmed) params.query = trimmed;
  if (filter !== 'all') params.installed = filter;

  const listing = useQuery({
    queryKey: qk.catalogList(kind, params as Record<string, unknown>),
    queryFn: () => listCatalog(kind, params),
  });

  // Shared polls: one HTTP request per list per 2s while any job is in
  // flight, regardless of how many cards are rendered.
  const activeJobs = useQuery({
    queryKey: qk.installJobsActive(),
    queryFn: listActiveInstallJobs,
    refetchInterval: (q) => {
      const data = q.state.data;
      return data && data.length > 0 ? 2000 : false;
    },
  });
  const activeEnvironmentJobs = useQuery({
    queryKey: qk.environmentJobsActive(),
    queryFn: listActiveEnvironmentJobs,
    refetchInterval: (q) => {
      const data = q.state.data;
      return data && data.length > 0 ? 2000 : false;
    },
  });

  // When both active lists drain (the last in-flight job ended),
  // revalidate the catalog so every card reads its terminal state.
  const previousActiveCount = useRef<number>(0);
  useEffect(() => {
    const current = (activeJobs.data?.length ?? 0) + (activeEnvironmentJobs.data?.length ?? 0);
    if (previousActiveCount.current > 0 && current === 0) {
      qc.invalidateQueries({ queryKey: ['catalog'] });
    }
    previousActiveCount.current = current;
  }, [activeJobs.data, activeEnvironmentJobs.data, qc]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ListToolbar
        search={query}
        onSearch={setQuery}
        placeholder="Search the catalog…"
        filters={<ChipFilter options={FILTERS} value={filter} onChange={setFilter} />}
        bare
      />
      <Body>
        {listing.isPending && <LoadingBlock />}
        {listing.isError && (
          <ErrorBlock
            message={
              listing.error instanceof Error ? listing.error.message : 'Failed to load catalog.'
            }
          />
        )}
        {listing.data && listing.data.length === 0 && (
          <EmptyBlock>
            {query || filter !== 'all'
              ? 'No matches for that search/filter.'
              : 'Nothing published yet. Use the Upload tab to publish a .tar.gz.'}
          </EmptyBlock>
        )}
        {listing.data && listing.data.length > 0 && (
          <div
            style={{ display: 'grid', gap: 11, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            {listing.data.map((entry: CatalogEntryOut) => (
              <MarketplaceCard key={entry.name} kind={kind} entry={entry} />
            ))}
          </div>
        )}
      </Body>
    </div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <div style={{ minHeight: '6rem' }}>{children}</div>;
}
